'use client'

import { cva } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import React from 'react'
import type { VariantProps } from 'class-variance-authority'
import { cn } from '@/utils'

const iconButtonVariants = cva(
  'inline-flex items-center justify-center transition-all cursor-pointer rounded-full disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-gray-600 text-white border border-gray-600 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-400 hover:bg-black active:bg-black active:text-gray-400',
        secondary:
          'bg-transparent text-gray-500 border border-gray-500 disabled:border-gray-400 disabled:text-gray-400 hover:border-black hover:text-black active:bg-gray-200 active:border-gray-500 active:text-gray-500',
        subtle:
          'bg-transparent text-black hover:bg-gray-100 active:bg-gray-200 disabled:text-gray-400 hover:text-gray-600 active:text-gray-600',
      },
      size: {
        sm: 'size-2xs [&_svg]:size-3.5',
        md: 'size-xs [&_svg]:size-4.5',
        lg: 'size-md [&_svg]:size-5.5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export type IconButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Visual style: `primary`, `secondary` or `subtle`. Defaults to `primary`. */
  variant?: VariantProps<typeof iconButtonVariants>['variant']
  /** Tap target size: `sm` 24px, `md` 32px, `lg` 48px. The icon scales with it. Defaults to `md`. */
  size?: VariantProps<typeof iconButtonVariants>['size']
  ref?: React.Ref<HTMLButtonElement>
  /**
   * Renders the single child element instead of a `<button>`, forwarding the
   * icon button classes to it — pass `asChild` with an `<a>` child to get a
   * link that looks like an icon button. Note that this is the mirror image of
   * wrapping `IconButton` in a Radix trigger such as `DrawerClose asChild`,
   * where the trigger is the one collapsing into the button. Defaults to
   * `false`.
   */
  asChild?: boolean
}

export const IconButton = ({
  variant,
  size,
  asChild = false,
  className,
  disabled,
  ...props
}: IconButtonProps) => {
  const Component = asChild ? Slot.Root : 'button'

  return (
    <Component
      className={cn(iconButtonVariants({ size, variant }), className)}
      disabled={disabled}
      {...props}
    />
  )
}
